import { FC, useState, useEffect } from "react";
import { TextField, Card, Flex, Text, Heading } from "@radix-ui/themes";
import { Button } from "@/components/button";
import { Loader2, Plus, X } from "lucide-react";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UpdateMonetizationSettingsRequestSchema,
  MonetizationSettingsResponseSchema,
} from "@/pages/monetization/types";
import { JsonValue } from "type-fest";

export const MonetizationPage: FC = () => {
  const [noteLimit, setNoteLimit] = useState(25);
  const [noteWarningLimit, setNoteWarningLimit] = useState<number | "">("");
  const [chatLimit, setChatLimit] = useState(25);
  const [chatWarningLimit, setChatWarningLimit] = useState<number | "">("");
  const [appleGroupId, setAppleGroupId] = useState<string>("");
  const [availableProducts, setAvailableProducts] = useState<
    { apple_product_id: string; stripe_price_id: string }[]
  >([{ apple_product_id: "", stripe_price_id: "" }]);
  const [message, setMessage] = useState<string>("");
  const queryClient = useQueryClient();
  const [appleRequestLoading, setAppleRequestLoading] = useState(false);
  const [appleRequestResult, setAppleRequestResult] = useState<JsonValue | null>(null);
  const [appleRequestError, setAppleRequestError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["monetization-settings"],
    queryFn: async () => {
      const response = await api.get("/internal/v1/monetization/current", {});
      return response as { data: MonetizationSettingsResponseSchema };
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateMonetizationSettingsRequestSchema) =>
      api.post("/internal/v1/monetization/update", {
        body: data,
      }),
    onSuccess: () => {
      setMessage("Monetization settings updated successfully");
      queryClient.invalidateQueries({ queryKey: ["monetization-settings"] });
    },
    onError: error => {
      setMessage(error?.message ?? "Failed to update monetization settings");
    },
  });

  useEffect(() => {
    // Update local state when settings are loaded
    if (settings?.data) {
      const configs = settings.data.feature_gate_configs;
      if (configs?.["note.create"]) {
        setNoteLimit(configs["note.create"].limits.free);
        setNoteWarningLimit(configs["note.create"].limits?.warning ?? "");
      }
      if (configs?.["chat_message.send"]) {
        setChatLimit(configs["chat_message.send"].limits.free);
        setChatWarningLimit(configs["chat_message.send"].limits?.warning ?? "");
      }

      // Load subscription settings - handle both missing field and null/undefined values
      const subs = settings.data.subscriptions;
      if (subs?.apple_group_id !== undefined) {
        setAppleGroupId(subs.apple_group_id ?? "");
        setAvailableProducts(
          subs.available_products && subs.available_products.length > 0
            ? subs.available_products
            : [{ apple_product_id: "", stripe_price_id: "" }]
        );
      } else {
        // Reset to defaults if no subscriptions exist or field is missing
        setAppleGroupId("");
        setAvailableProducts([{ apple_product_id: "", stripe_price_id: "" }]);
      }
    }
  }, [settings]);

  const handleSave = async () => {
    setMessage("");

    // Filter out products where both IDs are empty
    // Allow products with at least one ID filled
    const validProducts = availableProducts
      .filter(p => p.apple_product_id.trim() || p.stripe_price_id.trim())
      .map(p => ({
        apple_product_id: p.apple_product_id.trim() || "",
        stripe_price_id: p.stripe_price_id.trim() || "",
      }));

    const payload = {
      feature_gate_configs: {
        "note.create": {
          limits: {
            free: noteLimit,
            warning: noteWarningLimit === "" ? null : Number(noteWarningLimit),
          },
        },
        "chat_message.send": {
          limits: {
            free: chatLimit,
            warning: chatWarningLimit === "" ? null : Number(chatWarningLimit),
          },
        },
      },
      subscriptions:
        appleGroupId.trim() || validProducts.length > 0
          ? {
              apple_group_id: appleGroupId,
              available_products: validProducts,
            }
          : null,
    };

    updateMutation.mutate(payload);
  };

  const addProduct = () => {
    setAvailableProducts([...availableProducts, { apple_product_id: "", stripe_price_id: "" }]);
  };

  const removeProduct = (index: number) => {
    setAvailableProducts(availableProducts.filter((_, i) => i !== index));
  };

  const updateProduct = (
    index: number,
    field: "apple_product_id" | "stripe_price_id",
    value: string
  ) => {
    const updated = [...availableProducts];
    updated[index] = { ...updated[index], [field]: value };
    setAvailableProducts(updated);
  };

  const handleRequestAppleTestNotification = async () => {
    setAppleRequestError(null);
    setAppleRequestResult(null);
    setAppleRequestLoading(true);
    try {
      const resp = await api.post("/internal/v1/monetization/apple/request-test-notification");
      setAppleRequestResult((resp.data ?? resp) as JsonValue);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setAppleRequestError(message || "Request failed");
    } finally {
      setAppleRequestLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Container>
      <Content>
        <Heading size="6" mb="4">
          Monetization Settings
        </Heading>

        {message && (
          <MessageBox>
            <Text>{message}</Text>
          </MessageBox>
        )}

        <Card size="2">
          <Flex direction="column" gap="4">
            <div>
              <Heading size="3" mb="2">
                Free User Limits
              </Heading>
              <Text size="2" color="gray">
                Configure the monthly limits for free users. Paid users have unlimited access.
              </Text>
            </div>

            <Flex direction="column" gap="3">
              <div>
                <Text as="div" size="2" mb="1" weight="bold">
                  Monthly Note Creation Limit
                </Text>
                <TextField.Root
                  type="number"
                  min="0"
                  value={noteLimit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNoteLimit(isNaN(parseInt(e.target.value)) ? 0 : parseInt(e.target.value))
                  }
                  placeholder="Enter limit for free users"
                />
                <Text as="div" size="1" mt="2" color="gray">
                  Warning threshold before reaching the limit
                </Text>
                <TextField.Root
                  type="number"
                  min="0"
                  value={noteWarningLimit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNoteWarningLimit(
                      e.target.value === ""
                        ? ""
                        : isNaN(parseInt(e.target.value))
                          ? 0
                          : parseInt(e.target.value)
                    )
                  }
                  placeholder="Enter warning threshold (optional)"
                />
              </div>

              <div>
                <Text as="div" size="2" mb="1" weight="bold">
                  Monthly Chat Message Limit
                </Text>
                <TextField.Root
                  type="number"
                  min="0"
                  value={chatLimit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setChatLimit(isNaN(parseInt(e.target.value)) ? 0 : parseInt(e.target.value))
                  }
                  placeholder="Enter limit for free users"
                />
                <Text as="div" size="1" mt="2" color="gray">
                  Warning threshold before reaching the limit
                </Text>
                <TextField.Root
                  type="number"
                  min="0"
                  value={chatWarningLimit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setChatWarningLimit(
                      e.target.value === ""
                        ? ""
                        : isNaN(parseInt(e.target.value))
                          ? 0
                          : parseInt(e.target.value)
                    )
                  }
                  placeholder="Enter warning threshold (optional)"
                />
              </div>

              <div>
                <Heading size="3" mb="2">
                  Subscription Configuration
                </Heading>
                <Text size="2" color="gray">
                  Configure Apple and Stripe subscription settings
                </Text>

                <Flex direction="column" gap="3" mt="3">
                  <div>
                    <Text as="div" size="2" mb="1" weight="bold">
                      Apple Group ID
                    </Text>
                    <TextField.Root
                      value={appleGroupId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setAppleGroupId(e.target.value)
                      }
                      placeholder="com.example.app.subscription.group"
                    />
                  </div>

                  <div>
                    <Flex justify="between" mb="2">
                      <Text size="2" weight="bold">
                        Available Products
                      </Text>
                      <Button size="1" variant="soft" onClick={addProduct}>
                        <Plus size={14} style={{ marginRight: 4 }} />
                        Add Product
                      </Button>
                    </Flex>

                    <Flex direction="column" gap="2">
                      {availableProducts.map((product, index) => (
                        <Card key={index} size="1">
                          <Flex gap="2" align="center">
                            <Flex direction="column" gap="2" style={{ flex: 1 }}>
                              <TextField.Root
                                value={product.apple_product_id}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  updateProduct(index, "apple_product_id", e.target.value)
                                }
                                placeholder="Apple Product ID (e.g., com.example.app.monthly)"
                              />
                              <TextField.Root
                                value={product.stripe_price_id}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  updateProduct(index, "stripe_price_id", e.target.value)
                                }
                                placeholder="Stripe Price ID (e.g., price_123456789)"
                              />
                            </Flex>
                            <Button
                              size="1"
                              variant="ghost"
                              color="red"
                              onClick={() => removeProduct(index)}
                              disabled={availableProducts.length === 1}
                            >
                              <X size={16} />
                            </Button>
                          </Flex>
                        </Card>
                      ))}
                    </Flex>
                  </div>
                </Flex>
              </div>

              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 size={16} style={{ marginRight: 8 }} />}
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
              <Flex direction="column" gap="2">
                <Heading size="3" mb="1">
                  Apple StoreKit Notifications
                </Heading>
                <Text size="2" color="gray">
                  Send a test notification from Apple to our webhook.
                </Text>
                <Button onClick={handleRequestAppleTestNotification} disabled={appleRequestLoading}>
                  {appleRequestLoading && <Loader2 size={16} style={{ marginRight: 8 }} />}
                  {appleRequestLoading ? "Requesting..." : "Request Apple Test Notification"}
                </Button>
                {appleRequestError && (
                  <MessageBox>
                    <Text color="red">{appleRequestError}</Text>
                  </MessageBox>
                )}
                {appleRequestResult && (
                  <Card size="1">
                    <Pre>{JSON.stringify(appleRequestResult, null, 2)}</Pre>
                  </Card>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Card>

        {settings?.data && (
          <Card size="2" mt="4">
            <Flex direction="column" gap="3">
              <Heading size="3">Current Settings (Raw)</Heading>
              <Text size="2" color="gray">
                The complete monetization configuration
              </Text>
              <Pre>{JSON.stringify(settings.data, null, 2)}</Pre>
            </Flex>
          </Card>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MessageBox = styled.div`
  padding: 1rem;
  border-radius: 6px;
  background-color: var(--gray-3);
  border: 1px solid var(--gray-6);
`;

const Pre = styled.pre`
  background-color: var(--gray-3);
  padding: 1rem;
  border-radius: 6px;
  overflow: auto;
  font-size: 0.875rem;
  border: 1px solid var(--gray-6);
`;
